import { computed, inject, Injectable, signal } from '@angular/core';
import type { AuthSession, UserProfile } from '../models/auth.model';
import type { Company } from '../models/company.model';
import { StorageService } from './storage.service';

const SESSION_KEY = 'digimep.auth.session';
const PROFILE_KEY = 'digimep.auth.profile';
const COMPANY_KEY = 'digimep.auth.company';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly storage = inject(StorageService);
  private readonly sessionSignal = signal<AuthSession | null>(this.loadSession());
  private readonly profileSignal = signal<UserProfile | null>(
    this.storage.get<UserProfile>(PROFILE_KEY),
  );
  private readonly selectedCompanySignal = signal<Company | null>(
    this.storage.get<Company>(COMPANY_KEY),
  );

  readonly session = this.sessionSignal.asReadonly();
  readonly token = computed<string | null>(() => this.sessionSignal()?.token ?? null);
  readonly userRole = computed<string | null>(() => this.sessionSignal()?.userRole ?? null);
  readonly isAuthenticated = computed<boolean>(() => !!this.sessionSignal()?.token);

  readonly selectedCompany = this.selectedCompanySignal.asReadonly();

  readonly userProfile = this.profileSignal.asReadonly();
  readonly displayName = computed<string>(() => {
    const p = this.profileSignal();
    if (!p) return '';
    return [p.first_name, p.last_name].filter(Boolean).join(' ');
  });
  readonly initial = computed<string>(() => {
    return this.profileSignal()?.first_name?.charAt(0)?.toUpperCase() ?? '?';
  });
  readonly unreadCount = computed<number>(
    () => this.profileSignal()?.unread_message_count ?? 0,
  );

  setSelectedCompany(company: Company): void {
    this.selectedCompanySignal.set(company);
    this.storage.set(COMPANY_KEY, company);
  }

  setSession(session: AuthSession): void {
    this.sessionSignal.set(session);
    this.storage.set(SESSION_KEY, session);
  }

  setUserProfile(profile: UserProfile): void {
    this.profileSignal.set(profile);
    this.storage.set(PROFILE_KEY, profile);
  }

  decrementUnreadCount(by: number): void {
    const p = this.profileSignal();
    if (!p) return;
    const updated = { ...p, unread_message_count: Math.max(0, (p.unread_message_count ?? 0) - by) };
    this.profileSignal.set(updated);
    this.storage.set(PROFILE_KEY, updated);
  }

  clearSession(): void {
    this.sessionSignal.set(null);
    this.profileSignal.set(null);
    this.selectedCompanySignal.set(null);
    this.storage.remove(SESSION_KEY);
    this.storage.remove(PROFILE_KEY);
    this.storage.remove(COMPANY_KEY);
  }

  private loadSession(): AuthSession | null {
    return this.storage.get<AuthSession>(SESSION_KEY);
  }
}
